import { useEffect, useState } from 'react';
import { ActivityIndicator, StyleSheet, Switch } from 'react-native';
import { Badge } from '../../../components/primitives/Badge';
import { Box } from '../../../components/primitives/Box';
import { Button } from '../../../components/primitives/Button';
import { Divider } from '../../../components/primitives/Divider';
import { Input } from '../../../components/primitives/Input';
import { Select } from '../../../components/primitives/Select';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Text } from '../../../components/primitives/Text';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from '../../../contexts/TranslationContext';
import {
  useCreateLeadAssignment,
  useLeadAssignment,
  useUpdateLeadAssignment,
} from '../../../hooks/useLeadAssignment';
import { useSellerProfiles } from '../../../hooks/useSellerProfiles';
import type { AssignmentStrategy } from '../../../lib/api';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useMembers } from '../hooks/useSettings';
import { SettingsSection } from './SettingsSection';

function getInitials(fullName: string): string {
  if (!fullName) return '?';
  return fullName.charAt(0).toUpperCase();
}

interface FormState {
  strategy: AssignmentStrategy;
  isActive: boolean;
  assigneeUserIds: string[];
  maxLeadsPerUser: string;
}

const DEFAULT_FORM: FormState = {
  strategy: 'round_robin',
  isActive: true,
  assigneeUserIds: [],
  maxLeadsPerUser: '',
};

export function LeadAssignmentSection() {
  const { backendUser, currentOrganization } = useAuth();
  const { t } = useTranslation();
  const orgId = currentOrganization?.id;

  const memberships: Array<{
    organization?: { id: string };
    organizationId?: string;
    role: string;
  }> = backendUser?.organizationMembers ?? backendUser?.organizations ?? [];
  const orgMembership = memberships.find((o) => (o.organization?.id ?? o.organizationId) === orgId);
  const currentRole = orgMembership?.role ?? 'sales_rep';
  const canManage = currentRole === 'owner' || currentRole === 'admin';

  const { data: rule, isLoading: ruleLoading } = useLeadAssignment(orgId);
  const { data: membersResponse, isLoading: membersLoading } = useMembers(orgId);
  const { data: sellerProfiles } = useSellerProfiles(orgId);

  const createRule = useCreateLeadAssignment();
  const updateRule = useUpdateLeadAssignment();

  const members = membersResponse?.data ?? [];
  const profilesByUserId = new Map(sellerProfiles?.map((p) => [p.userId, p]) ?? []);

  const [form, setForm] = useState<FormState>(() => {
    if (rule) {
      return {
        strategy: rule.strategy,
        isActive: rule.isActive,
        assigneeUserIds: rule.assigneeUserIds,
        maxLeadsPerUser: rule.maxLeadsPerUser != null ? String(rule.maxLeadsPerUser) : '',
      };
    }
    return DEFAULT_FORM;
  });

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!ruleLoading && !initialized && rule) {
      setInitialized(true);
      setForm({
        strategy: rule.strategy,
        isActive: rule.isActive,
        assigneeUserIds: rule.assigneeUserIds,
        maxLeadsPerUser: rule.maxLeadsPerUser != null ? String(rule.maxLeadsPerUser) : '',
      });
    }
  }, [ruleLoading, initialized, rule]);

  const strategyOptions: { value: string; label: string }[] = [
    {
      value: 'round_robin',
      label: t('settings.leadAssignment.strategy.round_robin'),
    },
    {
      value: 'load_based',
      label: t('settings.leadAssignment.strategy.load_based'),
    },
    {
      value: 'manual_only',
      label: t('settings.leadAssignment.strategy.manual_only'),
    },
  ];

  const strategyDescriptions: Record<AssignmentStrategy, string> = {
    round_robin: t('settings.leadAssignment.strategy.round_robin_desc'),
    load_based: t('settings.leadAssignment.strategy.load_based_desc'),
    manual_only: t('settings.leadAssignment.strategy.manual_only_desc'),
  };

  const toggleAssignee = (userId: string) => {
    setForm((prev) => ({
      ...prev,
      assigneeUserIds: prev.assigneeUserIds.includes(userId)
        ? prev.assigneeUserIds.filter((id) => id !== userId)
        : [...prev.assigneeUserIds, userId],
    }));
  };

  const handleSave = () => {
    if (!orgId) return;

    const payload = {
      strategy: form.strategy,
      isActive: form.isActive,
      assigneeUserIds: form.assigneeUserIds,
      maxLeadsPerUser: form.maxLeadsPerUser ? Number(form.maxLeadsPerUser) : undefined,
    };

    if (rule) {
      updateRule.mutate(payload);
    } else {
      createRule.mutate({ ...payload, organizationId: orgId });
    }
  };

  const isSaving = createRule.isPending || updateRule.isPending;
  const isLoading = ruleLoading || membersLoading;

  if (isLoading) {
    return (
      <SettingsSection title={t('settings.leadAssignment.title')}>
        <Box alignItems="center" justifyContent="center" minHeight={120}>
          <ActivityIndicator size="large" color={colors.primary[500]} />
          <Text variant="bodySmall" color={colors.text.secondary} style={styles.loadingText}>
            {t('settings.leadAssignment.loading')}
          </Text>
        </Box>
      </SettingsSection>
    );
  }

  return (
    <SettingsSection
      title={t('settings.leadAssignment.title')}
      description={t('settings.leadAssignment.description')}
    >
      <VStack spacing="lg">
        <VStack spacing="xs">
          <HStack spacing="md" align="center" justify="between">
            <Text
              variant="body"
              weight="medium"
              color={colors.text.primary}
              style={styles.toggleLabel}
            >
              {t('settings.leadAssignment.enableAutoAssign')}
            </Text>
            {canManage ? (
              <Switch
                value={form.isActive}
                onValueChange={(value) => setForm((prev) => ({ ...prev, isActive: value }))}
                trackColor={{
                  false: colors.border.default,
                  true: colors.primary[500],
                }}
                thumbColor={colors.background.primary}
                accessibilityLabel={t('settings.leadAssignment.enableAutoAssign')}
              />
            ) : (
              <Badge color={form.isActive ? 'cyan' : 'neutral'} size="sm">
                {form.isActive ? t('common.states.on') : t('common.states.off')}
              </Badge>
            )}
          </HStack>
          <Text variant="caption" color={colors.text.secondary}>
            {t('settings.leadAssignment.enableAutoAssignDesc')}
          </Text>
        </VStack>

        <Divider color={colors.border.muted} />

        <VStack spacing="xs" style={styles.strategySelect}>
          <Text variant="bodySmall" color={colors.text.primary} style={styles.fieldLabel}>
            {t('settings.leadAssignment.strategyLabel')}
          </Text>
          {canManage ? (
            <Select
              variant="dropdown"
              options={strategyOptions}
              value={form.strategy}
              onChange={(v) => setForm((prev) => ({ ...prev, strategy: v as AssignmentStrategy }))}
            />
          ) : (
            <Text variant="body" color={colors.text.secondary}>
              {strategyOptions.find((o) => o.value === form.strategy)?.label ?? form.strategy}
            </Text>
          )}
          <Text variant="caption" color={colors.text.tertiary}>
            {strategyDescriptions[form.strategy]}
          </Text>
        </VStack>

        {form.strategy === 'load_based' && (
          <Input
            label={t('settings.leadAssignment.maxLeadsPerUser')}
            placeholder={t('settings.leadAssignment.maxLeadsPerUserPlaceholder')}
            value={form.maxLeadsPerUser}
            onChangeText={(v) =>
              setForm((prev) => ({ ...prev, maxLeadsPerUser: v.replace(/[^0-9]/g, '') }))
            }
            keyboardType="numeric"
            editable={canManage}
          />
        )}

        <Divider color={colors.border.muted} />

        <VStack spacing="sm">
          <Text variant="body" weight="medium" color={colors.text.primary}>
            {t('settings.leadAssignment.assigneePool')}
          </Text>
          <Text variant="caption" color={colors.text.secondary}>
            {t('settings.leadAssignment.assigneePoolDesc')}
          </Text>

          {members.length === 0 ? (
            <Text variant="body" color={colors.text.secondary}>
              {t('settings.leadAssignment.noMembers')}
            </Text>
          ) : (
            <VStack spacing="xs">
              {members.map((member) => {
                const selected = form.assigneeUserIds.includes(member.userId);
                const profile = profilesByUserId.get(member.userId);

                return (
                  <Box
                    key={member.userId}
                    pressable={canManage}
                    onPress={canManage ? () => toggleAssignee(member.userId) : undefined}
                    padding="sm"
                    borderRadius="lg"
                    backgroundColor={selected ? colors.primary.muted : colors.background.tertiary}
                    borderWidth={1}
                    borderColor={selected ? colors.primary[500] : colors.border.muted}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: selected }}
                  >
                    <HStack spacing="md" align="center">
                      <Box
                        width={36}
                        height={36}
                        borderRadius="full"
                        backgroundColor={
                          selected ? colors.primary[500] : colors.background.elevated
                        }
                        alignItems="center"
                        justifyContent="center"
                      >
                        <Text
                          variant="bodySmall"
                          weight="semibold"
                          color={selected ? colors.background.primary : colors.text.secondary}
                        >
                          {getInitials(member.fullName)}
                        </Text>
                      </Box>

                      <VStack spacing="xxs" style={styles.memberInfo}>
                        <Text
                          variant="body"
                          weight="medium"
                          color={colors.text.primary}
                          numberOfLines={1}
                          ellipsizeMode="tail"
                        >
                          {member.fullName}
                        </Text>
                        <Text
                          variant="caption"
                          color={colors.text.secondary}
                          numberOfLines={1}
                          ellipsizeMode="tail"
                        >
                          {member.email}
                        </Text>
                      </VStack>

                      <HStack spacing="xs" align="center">
                        {profile && (
                          <Badge color="blue" size="sm">
                            {profile.sellerType}
                          </Badge>
                        )}
                        {selected && (
                          <Badge color="cyan" size="sm">
                            {t('settings.leadAssignment.selected')}
                          </Badge>
                        )}
                      </HStack>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>
          )}
        </VStack>

        {(createRule.isError || updateRule.isError) && (
          <Text variant="caption" color={colors.status.error.main}>
            {t('settings.leadAssignment.saveError')}
          </Text>
        )}

        {canManage && (
          <HStack spacing="sm" justify="end">
            <Button
              variant="primary"
              size="md"
              onPress={handleSave}
              loading={isSaving}
              disabled={isSaving || form.assigneeUserIds.length === 0}
            >
              {t('common.actions.saveChanges')}
            </Button>
          </HStack>
        )}
      </VStack>
    </SettingsSection>
  );
}

const styles = StyleSheet.create({
  loadingText: {
    marginTop: tokens.spacing.md,
  },
  toggleLabel: {
    flexShrink: 1,
  },
  strategySelect: {
    zIndex: 10,
  },
  fieldLabel: {
    fontWeight: '500',
  },
  memberInfo: {
    flex: 1,
    minWidth: 0,
  },
});
